import apiClient from './api';
import aes from '@/utils/aes';

const authService = {
  /**
   * 使用者註冊
   * @param {Object} userData - { Account, Password, ConfirmPassword, Email }
   * @returns {Promise}
   */
  async register(userData) {
    try {      
      const response = await apiClient.post('/User/Register', {
        Account: userData.account || userData.Account,
        Password: userData.password || userData.Password,
        ConfirmPassword: userData.confirmPassword || userData.ConfirmPassword,
        Email: userData.email || userData.Email,
      });
      
      const message = response.data.message || response.data.Message || 'Registration successful. Please check your email for activation link.';
      
      return {
        success: true,
        data: response.data,
        message: message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.Message || 'Registration failed. Please try again.',
        errors: error.response?.data?.errors || error.response?.data?.Errors || {},
      };
    }
  },

  /**
   * 使用者登入
   * @param {Object} credentials - { Account, Password }
   * @returns {Promise}
   */
  async login(credentials) {
    try {
      const response = await apiClient.post('/User/Login', {
        Account: credentials.Account,
        Password: credentials.Password,
      });
      
      // 處理 PascalCase 和 camelCase 格式的回應
      const loginJWT = response.data.loginJWT || response.data.LoginJWT;
      const loginToken = response.data.loginToken || response.data.LoginToken;
      const account = response.data.account || response.data.Account;
      const name = response.data.name || response.data.Name;
      const email = response.data.email || response.data.Email;
      
      if (loginJWT) {
        // 儲存認證資料
        localStorage.setItem('loginJWT', loginJWT);
        localStorage.setItem('loginToken', loginToken);
        localStorage.setItem('userInfo', JSON.stringify({
          Account: account,
          Name: name,
          Email: email,
        }));
        
        return {
          success: true,
          data: {
            LoginJWT: loginJWT,
            LoginToken: loginToken,
            Account: account,
            Name: name,
            Email: email,
          },
          message: 'Login successful',
        };
      } else {
        return {
          success: false,
          message: 'Login failed. Invalid response from server.',
        };
      }
    } catch (error) {
      // 處理後端回傳的特定錯誤訊息
      let errorMessage = 'Login failed. Please check your credentials.';
      
      if (error.response?.data?.message || error.response?.data?.Message) {
        errorMessage = error.response.data.message || error.response.data.Message;
      }
      
      return {
        success: false,
        message: errorMessage,
        errors: error.response?.data?.errors || error.response?.data?.Errors || {},
      };
    }
  },

  /**
   * 啟用使用者帳號
   * @param {Object} activationData - { Account, ActiveCode }
   * @returns {Promise}
   */
  async activateAccount(activationData) {
    try {
      const response = await apiClient.post('/User/Activate', {
        Account: activationData.account || activationData.Account,
        ActiveCode: activationData.activeCode || activationData.ActiveCode,
      });
      
      const message = response.data.message || response.data.Message || 'Account activated successfully!';
      
      return {
        success: true,
        data: response.data,
        message: message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.Message || 'Invalid activation link.',
      };
    }
  },

  /**
   * 忘記密碼 - 發送重設信件
   * @param {string} email
   * @returns {Promise}
   */
  async forgotPassword(email) {
    try {
      const response = await apiClient.post('/User/ForgotPassword', {
        Email: email,
      });
      
      const message = response.data.message || response.data.Message || 'If this email is registered, you will receive a password reset link.';
      
      return {
        success: true,
        data: response.data,
        message: message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.Message || 'Failed to send reset email. Please try again.',
      };
    }
  },

  /**
   * 重設密碼
   * @param {Object} resetData - { Account, NewPassword, ConfirmPassword, ResetPasswordCode }
   * @returns {Promise}
   */
  async resetPassword(resetData) {
    try {
      const response = await apiClient.post('/User/ResetPassword', {
        Account: resetData.account || resetData.Account,
        NewPassword: resetData.newPassword || resetData.NewPassword,
        ConfirmPassword: resetData.confirmPassword || resetData.ConfirmPassword,
        ResetPasswordCode: resetData.resetPasswordCode || resetData.ResetPasswordCode,
      });
      
      const message = response.data.message || response.data.Message || 'Password reset successfully!';
      
      return {
        success: true,
        data: response.data,
        message: message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.Message || 'Invalid reset link or passwords do not match.',
      };
    }
  },

  /**
   * 更新密碼 (已登入使用者)
   * @param {Object} passwordData - { OldPassword, NewPassword, ConfirmPassword }
   * @returns {Promise}
   */
  async updatePassword(passwordData) {
    try {      
      console.log('Updating password with data:', passwordData);
      const response = await apiClient.put('/User/ChangePassword', {
        OldPassword: passwordData.oldPassword || passwordData.OldPassword,
        NewPassword: passwordData.newPassword || passwordData.NewPassword,
        ConfirmPassword: passwordData.confirmPassword || passwordData.ConfirmPassword,
      });
      
      const message = response.data.message || response.data.Message || 'Password updated successfully!';
      
      return {
        success: true,
        data: response.data,
        message: message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.Message || 'Failed to update password. Please check your current password.',
        errors: error.response?.data?.errors || error.response?.data?.Errors || {},
      };
    }
  },

  async updateReview(isReview) {
    try {
      const response = await apiClient.put('/User/UpdateReview', { isReview });
      
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
      };
    }
  },
  
  async updateAgree(isAgree) {
    try {
      const response = await apiClient.put('/User/UpdateAgree', { isAgree });
      
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
      };
    }
  },

  /**
   * 清空使用者的資格審核及同意條款狀態
   * 當使用者選擇學期梯次時使用
   */
  async clearReviewAndAgree() {
    try {
      const response = await apiClient.put('/User/ClearReviewAndAgree');
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Clear review and agree error:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.response?.data?.Message || 'Failed to clear review and agree status',
      };
    }
  },

  /**
   * 登出
   */
  logout() {
    localStorage.removeItem('loginJWT');
    localStorage.removeItem('loginToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('selectedApplyId'); // 清除選擇的學期梯次
  },

  /**
   * 檢查使用者是否已認證
   * @returns {boolean}
   */
  isAuthenticated() {
    const token = localStorage.getItem('loginJWT');
    return !!token;
  },

  /**
   * 取得目前使用者資訊
   * @returns {Object|null}
   */
  getCurrentUser() {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  },
};

export default authService;
